<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { roundAwayFromZero } from '@Shared/utils/util';
import VisualDelta from '@Shared/components/visuals/parts/VisualDelta.vue';
import { useFilterStore } from '@Portal/stores/filter';
import { storeToRefs } from 'pinia';
import { getImageUrl } from './utility';

interface SocialReviewRepliesViewModel {
  count: number;
  title: string;
  percentageReplied: MeasureResult;
  averageResponseTimeInHours?: MeasureResult;
}

const props = defineProps<SocialReviewRepliesViewModel>();
const { percentageReplied, averageResponseTimeInHours } = toRefs(props);

const repliedReviews = computed<string | '-'>(() =>
  percentageReplied.value.currentValue || percentageReplied.value.currentValue == 0
    ? roundAwayFromZero(percentageReplied.value.currentValue * 100) + '%'
    : '-'
);

const repliedReviewsDelta = computed<number>(() =>
  roundAwayFromZero((percentageReplied.value.currentValue! - percentageReplied.value.previousValue!) * 100)
);

const responseTime = ref<string>('-');
const responseTimeDelta = ref<number | null>(null);

watchEffect(() => {
  if (
    averageResponseTimeInHours.value != undefined &&
    averageResponseTimeInHours.value.currentValue != undefined &&
    !Number.isNaN(averageResponseTimeInHours.value.currentValue)
  ) {
    let denominator = 1;
    let timeScale = 'hour';
    const hours = averageResponseTimeInHours.value.currentValue;
    const previousHours = averageResponseTimeInHours.value.previousValue!;

    const hoursRounded = roundAwayFromZero(hours);
    if (hoursRounded >= 24 * 7 * 4) {
      timeScale = 'month';
      denominator = 24 * 7 * 4;
    } else if (hoursRounded >= 24 * 7) {
      timeScale = 'week';
      denominator = 24 * 7;
    } else if (hoursRounded >= 24) {
      timeScale = 'day';
      denominator = 24;
    }

    // If the unit is < 1, then we know for sure that the time scale is in hours
    // as we only change the time scale if we know the unit is >= 1
    const responseTimeUnit = roundAwayFromZero(hours / denominator);
    if (responseTimeUnit < 1) {
      responseTime.value = 'Less than an hour';
    } else {
      if (responseTimeUnit != 1) timeScale += 's';
      responseTime.value = responseTimeUnit + ' ' + timeScale;
    }

    const responseTimeDeltaTemp = (hours - previousHours) / denominator;
    if (responseTimeDeltaTemp < 0) {
      responseTimeDelta.value = -roundAwayFromZero(-responseTimeDeltaTemp);
    } else {
      responseTimeDelta.value = roundAwayFromZero(responseTimeDeltaTemp);
    }
  } else {
    responseTime.value = '-';
    responseTimeDelta.value = null;
  }
});

const filterStore = useFilterStore();
const { dateFilterDisplayValue } = storeToRefs(filterStore);
</script>

<template>
  <div
    class="review-replies stretch-height"
    :style="`background: url(${getImageUrl('/social/repliesToReviewsBackground.svg')})`"
    v-if="percentageReplied.currentValue !== undefined"
  >
    <div class="visual-title">Replies to Reviews {{ dateFilterDisplayValue }}</div>
    <div class="review-replies-box">
      <div class="reply-section">
        <span class="reply-section-title">Percentage of replied reviews:</span>
        <span class="social-box">{{ repliedReviews }}</span>
        <VisualDelta :value="repliedReviewsDelta" style="padding-right: 5px"></VisualDelta>
      </div>
      <div class="reply-section">
        <span class="reply-section-title">Average response time:</span>
        <span class="social-box">{{ responseTime }}</span>
        <VisualDelta :value="responseTimeDelta!" :invert="true" style="padding-right: 5px"></VisualDelta>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.review-replies {
  .review-replies-box {
    margin-top: 42px;
    margin-left: 40px;
    font-size: 18px;
    font-weight: 600;
    line-height: 125%;
  }

  .reply-section {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    width: fit-content;
    color: #4a4a4a;

    .reply-section-title {
      flex: 1;
    }
  }

  .social-box {
    font-size: 25px;
    font-weight: 700;
    color: white;
    background: #c73362;
    border-radius: 3px;
    padding: 2px 8px 3px;
    margin-left: 8px;
    margin-right: 6px;
    line-height: 125%;
    vertical-align: bottom;
    white-space: nowrap;
  }

  @media only screen and (max-width: 479px) {
    .review-replies-box {
      margin-left: 22px;
      margin-top: 22px;
      font-size: 16px;
      margin-right: 20px;
    }

    .reply-section {
      margin-bottom: 16px;
    }

    .social-box {
      font-size: 20px;
    }
  }
}
</style>
